import { useState, useEffect, FC, useMemo, useCallback } from 'react';
import {
  GovernanceTagsContext,
  GovernanceTagsContextType,
  GovernanceTagDistinctValues,
  GovernanceTagsProviderProps,
  FilterParams,
  FilterOption,
} from '../types';

const defaultHiddenColumns: string[] = [];

export const GovernanceTagsProvider: FC<GovernanceTagsProviderProps> = ({ children }) => {
  const [governanceTagsFilters, setGovernanceTagsFilters] = useState<{ [key: string]: FilterOption[] }>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const filterContextParam = searchParams.get('filterContext');
    if (filterContextParam) {
      return JSON.parse(filterContextParam);
    }
    return {};
  });
  const [isFiltersChanged, setIsFiltersChanged] = useState<boolean>(false);
  const [hiddenColumnNames, setHiddenColumnNames] = useState<string[]>([...defaultHiddenColumns]);
  const [showCustomColumnsNames, setShowCustomColumnsNames] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<GovernanceTagsContextType['metrics']>(null);
  const [distinctTagValues, setDistinctTagValues] = useState<GovernanceTagDistinctValues | null>(null);
  const [filterParams, setFilterParams] = useState<FilterParams>({});

  const initFiltersState = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const filterContextParam = searchParams.get('filterContext');
    if (filterContextParam) {
      return JSON.parse(filterContextParam);
    }
    return {};
  };

  const getActiveFiltersCount = useCallback((): number => {
    let count = 0;
    Object.values(governanceTagsFilters).forEach((filterOptions) => {
      if (Array.isArray(filterOptions) && filterOptions.length > 0) {
        count += filterOptions.length;
      }
    });
    return count;
  }, [governanceTagsFilters]);

  const convertFiltersToParams = useCallback((filters: { [key: string]: FilterOption[] }): FilterParams => {
    const params: FilterParams = {};

    const filterIdToParamKey: { [key: string]: keyof FilterParams } = {
      service: 'service',
      region: 'region',
      linkedaccid: 'linkedAccountId',
      resourcename: 'resourceName',
      resourceid: 'resourceId',
      tags: 'tags',
      governancetags: 'governanceTags',
    };

    Object.entries(filters).forEach(([key, options]) => {
      if (options && options.length > 0) {
        const values = options.map((option) => option.value);
        const paramKey = filterIdToParamKey[key];

        if (paramKey) {
          params[paramKey] = values;
        }
      }
    });

    return params;
  }, []);

  const addNewTagKey = useCallback(
    (key: string) => {
      if (!key || !distinctTagValues) return;

      setDistinctTagValues((prev) => {
        if (!prev || prev[key]) return prev;

        return {
          ...prev,
          [key]: [],
        };
      });
    },
    [distinctTagValues],
  );

  const addNewTagValue = useCallback(
    (value: string) => {
      if (!value || !distinctTagValues) return;

      setDistinctTagValues((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach((key) => {
          if (!updated[key].includes(value)) {
            updated[key] = [...updated[key], value];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    },
    [distinctTagValues],
  );

  const addNewTagKeyValue = useCallback(
    (key: string, value: string) => {
      if (!key || !value || !distinctTagValues) return;

      setDistinctTagValues((prev) => {
        if (!prev) return prev;

        const updated = { ...prev };

        if (!updated[key]) {
          updated[key] = [];
        }

        if (!updated[key].includes(value)) {
          updated[key] = [...updated[key], value];
        }

        return updated;
      });
    },
    [distinctTagValues],
  );

  useEffect(() => {
    const initialFilters = initFiltersState();
    const isChanged = Object.keys(governanceTagsFilters).length !== Object.keys(initialFilters).length;
    setIsFiltersChanged(isChanged);

    const updateFilterParamsOnChange = convertFiltersToParams(governanceTagsFilters);
    setFilterParams(updateFilterParamsOnChange);
  }, [governanceTagsFilters, convertFiltersToParams]);

  const filterState = useMemo(
    () => ({
      isFiltersChanged,
      governanceTagsFilters,
      setGovernanceTagsFilters,
      filterParams,
      setFilterParams,
      getActiveFiltersCount,
    }),
    [isFiltersChanged, governanceTagsFilters, filterParams, getActiveFiltersCount],
  );

  const columnState = useMemo(
    () => ({
      hiddenColumnNames,
      setHiddenColumnNames,
      showCustomColumnsNames,
      setShowCustomColumnsNames,
    }),
    [hiddenColumnNames, showCustomColumnsNames],
  );

  const dataState = useMemo(
    () => ({
      metrics,
      setMetrics,
      distinctTagValues,
      setDistinctTagValues,
      addNewTagKey,
      addNewTagValue,
      addNewTagKeyValue,
    }),
    [metrics, distinctTagValues, addNewTagKey, addNewTagValue, addNewTagKeyValue],
  );

  const contextValue = useMemo(
    () => ({
      ...filterState,
      ...columnState,
      ...dataState,
    }),
    [filterState, columnState, dataState],
  );

  return <GovernanceTagsContext.Provider value={contextValue}>{children}</GovernanceTagsContext.Provider>;
};
